export const STAFF_CUSTOMER_ACCESS_FIELD = "customerAccess";

// Admin document shape:
// customerAccess: { [nodeCode]: null | customerDocumentId[] }
// null means all customers for that node; a missing customerAccess field keeps
// the legacy behavior of all customers in every assigned node.

const CUSTOMER_ACCESS_ELEVATED_ROLES = [
  "super_admin",
  "sale",
  "accountant",
  "sleep_physician",
  "sleep_technician",
];

const hasOwn = (value, key) =>
  Boolean(value) && Object.prototype.hasOwnProperty.call(value, key);

export const normalizeAccessValue = (value) =>
  String(value === undefined || value === null ? "" : value)
    .normalize("NFKC")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeMatchValue = (value) => normalizeAccessValue(value).toLowerCase();

export const normalizeAccessList = (value) => {
  const values = Array.isArray(value) ? value : [value];

  return values.reduce((result, item) => {
    const normalized = normalizeAccessValue(item);
    if (normalized && !result.includes(normalized)) result.push(normalized);
    return result;
  }, []);
};

export const getUserRoles = (currentUser) =>
  currentUser && Array.isArray(currentUser.roles) ? currentUser.roles : [];

// Keep the existing helper name for callers; staff and admin share this scope.
export const isStaffCustomerScopedUser = (currentUser) => {
  const roles = getUserRoles(currentUser);

  return (
    roles.some((role) => ["staff", "admin"].includes(role)) &&
    !roles.some((role) => CUSTOMER_ACCESS_ELEVATED_ROLES.includes(role))
  );
};

export const getCustomerAccessMap = (adminData) => {
  if (!adminData || !hasOwn(adminData, STAFF_CUSTOMER_ACCESS_FIELD)) {
    return null;
  }

  const access = adminData[STAFF_CUSTOMER_ACCESS_FIELD];
  return access && typeof access === "object" && !Array.isArray(access)
    ? access
    : null;
};

export const hasExplicitStaffCustomerAccess = (adminData) =>
  getCustomerAccessMap(adminData) !== null;

const getRestrictedCustomerIds = (rawAccess) => {
  if (Array.isArray(rawAccess)) return normalizeAccessList(rawAccess);

  if (rawAccess && typeof rawAccess === "object") {
    if (rawAccess.allCustomers === true) return null;
    return normalizeAccessList(rawAccess.customerIds || []);
  }

  return [];
};

export const getNodeCustomerAccess = (adminData, nodeCode) => {
  const accessMap = getCustomerAccessMap(adminData);
  const normalizedNodeCode = normalizeAccessValue(nodeCode);

  // Missing field is the legacy-compatible "all customers" behavior.
  if (accessMap === null) {
    return { mode: "all", customerIds: [], isLegacyDefault: true };
  }

  if (!normalizedNodeCode || !hasOwn(accessMap, normalizedNodeCode)) {
    return { mode: "none", customerIds: [], isLegacyDefault: false };
  }

  const rawAccess = accessMap[normalizedNodeCode];
  if (rawAccess === null || rawAccess === "*") {
    return { mode: "all", customerIds: [], isLegacyDefault: false };
  }

  const customerIds = getRestrictedCustomerIds(rawAccess);
  if (customerIds === null) {
    return { mode: "all", customerIds: [], isLegacyDefault: false };
  }

  return {
    mode: customerIds.length > 0 ? "selected" : "none",
    customerIds,
    isLegacyDefault: false,
  };
};

export const canAccessNodeCustomer = (adminData, nodeCode, customerId) => {
  const access = getNodeCustomerAccess(adminData, nodeCode);
  if (access.mode === "all") return true;
  if (access.mode === "none") return false;

  const customerKey = normalizeAccessValue(customerId);
  return (
    Boolean(customerKey) &&
    access.customerIds.some(
      (allowedCustomerId) =>
        normalizeAccessValue(allowedCustomerId) === customerKey,
    )
  );
};

export const resolveNodeCode = (nodes, nodeValue) => {
  const nodeKey = normalizeMatchValue(nodeValue);
  if (!nodeKey) return "";

  const matchedNode = (nodes || []).find((node) =>
    [node && node.nodecode, node && node.nodename, node && node.id].some(
      (candidate) => normalizeMatchValue(candidate) === nodeKey,
    ),
  );

  return matchedNode ? normalizeAccessValue(matchedNode.nodecode) : "";
};

export const getAssignedStaffNodeCodes = (adminData, nodes) =>
  normalizeAccessList(adminData && adminData.node).reduce(
    (assignedNodeCodes, nodeValue) => {
      const nodeCode =
        resolveNodeCode(nodes, nodeValue) || normalizeAccessValue(nodeValue);

      if (nodeCode && !assignedNodeCodes.includes(nodeCode)) {
        assignedNodeCodes.push(nodeCode);
      }

      return assignedNodeCodes;
    },
    [],
  );

export const filterRecordsByStaffCustomerAccess = ({
  records,
  currentUser,
  adminData,
  nodes,
  getNodeValue,
  getCustomerValue,
}) => {
  const source = Array.isArray(records) ? records : [];

  if (!isStaffCustomerScopedUser(currentUser)) return source;

  // Customer access always sits inside the staff/admin account's assigned nodes.
  // Keeping this check here makes every caller legacy-safe: an old account
  // without customerAccess sees all customers, but only in its own nodes.
  if (!adminData) return [];

  const assignedNodeCodes = getAssignedStaffNodeCodes(adminData, nodes);
  if (assignedNodeCodes.length === 0) return [];

  const nodeScopedRecords = source.filter((record) => {
    const nodeCode = resolveNodeCode(
      nodes,
      getNodeValue ? getNodeValue(record) : "",
    );

    return Boolean(nodeCode) && assignedNodeCodes.includes(nodeCode);
  });

  if (!hasExplicitStaffCustomerAccess(adminData)) {
    return nodeScopedRecords;
  }

  return nodeScopedRecords.filter((record) => {
    const nodeCode = resolveNodeCode(
      nodes,
      getNodeValue ? getNodeValue(record) : "",
    );
    const customerId = getCustomerValue ? getCustomerValue(record) : "";

    return canAccessNodeCustomer(adminData, nodeCode, customerId);
  });
};

export const filterCustomersForStaffNode = ({
  customers,
  currentUser,
  adminData,
  nodeCode,
  getCustomerId,
}) => {
  const source = Array.isArray(customers) ? customers : [];

  if (!isStaffCustomerScopedUser(currentUser)) return source;
  if (!adminData) return [];

  const assignedNodeCodes = normalizeAccessList(adminData.node);
  if (!assignedNodeCodes.includes(normalizeAccessValue(nodeCode))) return [];

  if (!hasExplicitStaffCustomerAccess(adminData)) return source;

  const access = getNodeCustomerAccess(adminData, nodeCode);
  if (access.mode === "all") return source;
  if (access.mode === "none") return [];

  return source.filter((customer) =>
    canAccessNodeCustomer(
      adminData,
      nodeCode,
      getCustomerId ? getCustomerId(customer) : customer,
    ),
  );
};

export const createCustomerAccessDraft = (adminData, nodeCodes) => {
  const accessMap = getCustomerAccessMap(adminData);
  if (accessMap === null) return {};

  return normalizeAccessList(nodeCodes).reduce((draft, nodeCode) => {
    if (!hasOwn(accessMap, nodeCode)) {
      // An explicit map with no entry means no access for that node.
      draft[nodeCode] = [];
      return draft;
    }

    const rawAccess = accessMap[nodeCode];
    if (rawAccess !== null && rawAccess !== "*") {
      const customerIds = getRestrictedCustomerIds(rawAccess);
      if (customerIds !== null) draft[nodeCode] = customerIds;
    }

    return draft;
  }, {});
};

export const buildCustomerAccessField = (draft, nodeCodes) => {
  const selectedNodeCodes = normalizeAccessList(nodeCodes);
  const restrictedNodeCodes = selectedNodeCodes.filter((nodeCode) =>
    hasOwn(draft, nodeCode),
  );

  // No restricted nodes: omit/delete the field to retain legacy "all" access.
  if (restrictedNodeCodes.length === 0) return null;

  return selectedNodeCodes.reduce((accessMap, nodeCode) => {
    accessMap[nodeCode] = hasOwn(draft, nodeCode)
      ? normalizeAccessList(draft[nodeCode])
      : null;
    return accessMap;
  }, {});
};
